/**
 * Watch & Shop Reels — JavaScript Asset
 * Premium Lab-Grown Diamond Jewelry
 * watch-shop-reels.js
 *
 * Features:
 *   • Drag/swipe carousel with momentum
 *   • Hover video preview (desktop)
 *   • Full-screen reel viewer with swipe nav
 *   • Video autoplay / pause management
 *   • Progress bar
 *   • Share & Wishlist actions
 *   • Product reveal / Add-to-cart
 *   • CSS custom property injection from schema settings
 */

'use strict';

(function () {
  /* ── Utility ──────────────────────────────────────────── */
  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function clamp(val, min, max) { return Math.min(Math.max(val, min), max); }

  /* ══════════════════════════════════════════════════════
     CLASS: WatchShopReels
     Manages the homepage cards carousel
  ══════════════════════════════════════════════════════ */
  class WatchShopReels {
    constructor(section) {
      this.section      = section;
      this.sectionId    = section.dataset.sectionId;
      this.carousel     = qs('[data-carousel]',      section);
      this.track        = qs('[data-track]',         section);
      this.cards        = qsa('.wsr-card',           section);
      this.dotsWrap     = qs('[data-dots]',          section);
      this.dots         = qsa('[data-dot]',          section);
      this.prevBtn      = qs('[data-carousel-prev]', section);
      this.nextBtn      = qs('[data-carousel-next]', section);

      if (!this.track || !this.cards.length) return;

      this.currentIndex = 0;
      this.isDragging   = false;
      this.startX       = 0;
      this.scrollStart  = 0;
      this.momentum     = 0;
      this.rafId        = null;

      this._readSettings();
      this._applyStyles();
      this._bindCarousel();
      this._bindHoverVideos();
      this._bindOpenViewer();
      this._updateNav();
    }

    /* ── Read data attributes from Shopify schema ── */
    _readSettings() {
      const c = this.carousel;
      this.desktopCards = parseInt(c.dataset.desktopCards) || 5;
      this.mobileCards  = parseInt(c.dataset.mobileCards)  || 2;
      this.gap          = parseInt(c.dataset.gap)           || 16;
    }

    /* ── Apply CSS vars for column widths ── */
    _applyStyles() {
      const s = this.section;
      s.style.setProperty('--wsr-desktop-cards', this.desktopCards);
      s.style.setProperty('--wsr-gap', this.gap + 'px');

      // Inject section-level colour vars from inline style or data (set by liquid)
      const accentColor = s.dataset.accentColor;
      const bgColor     = s.dataset.bgColor;
      const textColor   = s.dataset.textColor;
      if (accentColor) s.style.setProperty('--wsr-accent', accentColor);
      if (bgColor)     s.style.setProperty('--wsr-bg',     bgColor);
      if (textColor)   s.style.setProperty('--wsr-clr',    textColor);
    }

    /* ── Calculate card width ── */
    _getCardWidth() {
      const isMobile = window.innerWidth < 768;
      const cols     = isMobile ? this.mobileCards : this.desktopCards;
      const padding  = isMobile ? 48 : 128;
      const totalGap = (cols - 1) * this.gap;
      return (this.carousel.offsetWidth - padding - totalGap) / cols;
    }

    /* ── Set card widths ── */
    _setCardWidths() {
      const w = this._getCardWidth();
      this.cards.forEach(card => { card.style.width = w + 'px'; });
    }

    /* ── Scroll track to index ── */
    _scrollTo(index) {
      const cardW  = this._getCardWidth();
      const offset = index * (cardW + this.gap);
      const maxOff = (this.cards.length - (window.innerWidth < 768 ? this.mobileCards : this.desktopCards)) * (cardW + this.gap);
      const x      = clamp(offset, 0, Math.max(maxOff, 0));
      this.track.style.transform = `translateX(-${x}px)`;
      this.currentIndex = index;
      this._updateNav();
      this._updateDots();
    }

    _updateNav() {
      const maxIndex = Math.max(0, this.cards.length - (window.innerWidth < 768 ? this.mobileCards : this.desktopCards));
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }

    _updateDots() {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.currentIndex));
    }

    /* ── Carousel bindings ── */
    _bindCarousel() {
      this._setCardWidths();
      window.addEventListener('resize', () => {
        this._setCardWidths();
        this._scrollTo(this.currentIndex);
      });

      /* Nav buttons */
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._scrollTo(Math.max(0, this.currentIndex - 1)));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._scrollTo(this.currentIndex + 1));

      /* Dots */
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => this._scrollTo(parseInt(dot.dataset.dot)));
      });

      /* Touch / mouse drag */
      const el = this.carousel;

      const onStart = (clientX) => {
        this.isDragging  = true;
        this.startX      = clientX;
        this.scrollStart = this.currentIndex;
        el.style.cursor  = 'grabbing';
        cancelAnimationFrame(this.rafId);
      };

      const onMove = (clientX) => {
        if (!this.isDragging) return;
        const cardW = this._getCardWidth();
        const delta = this.startX - clientX;
        const step  = delta / (cardW + this.gap);
        const rawX  = (this.scrollStart + step) * (cardW + this.gap);
        const maxX  = (this.cards.length - 1) * (cardW + this.gap);
        this.track.style.transform = `translateX(-${clamp(rawX, 0, maxX)}px)`;
        this.momentum = clientX;
      };

      const onEnd = (clientX) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        el.style.cursor = 'grab';
        const cardW = this._getCardWidth();
        const delta = this.startX - clientX;
        const step  = delta / (cardW + this.gap);
        const snapped = Math.round(this.scrollStart + step);
        const maxIndex = Math.max(0, this.cards.length - (window.innerWidth < 768 ? this.mobileCards : this.desktopCards));
        this._scrollTo(clamp(snapped, 0, maxIndex));
      };

      el.addEventListener('mousedown',  e => onStart(e.clientX));
      window.addEventListener('mousemove', e => { if (this.isDragging) onMove(e.clientX); });
      window.addEventListener('mouseup',   e => { if (this.isDragging) onEnd(e.clientX); });
      el.addEventListener('touchstart',  e => onStart(e.touches[0].clientX), { passive: true });
      el.addEventListener('touchmove',   e => { if (this.isDragging) onMove(e.touches[0].clientX); }, { passive: true });
      el.addEventListener('touchend',    e => onEnd(e.changedTouches[0].clientX));

      /* Keyboard */
      el.setAttribute('tabindex', '0');
      el.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._scrollTo(Math.max(0, this.currentIndex - 1)); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._scrollTo(this.currentIndex + 1); }
      });
    }

    /* ── Hover Video Preview ── */
    _bindHoverVideos() {
      this.cards.forEach(card => {
        const video = qs('.wsr-card__video', card);
        if (!video) return;

        card.addEventListener('mouseenter', () => {
          card.classList.add('is-playing');
          video.currentTime = 0;
          video.play().catch(() => {});
        });

        card.addEventListener('mouseleave', () => {
          card.classList.remove('is-playing');
          video.pause();
        });
      });
    }

    /* ── Open Viewer on click ── */
    _bindOpenViewer() {
      qsa('[data-open-reel]', this.section).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const reelIndex = parseInt(btn.dataset.openReel);
          wsrViewer.open(reelIndex);
        });
      });

      /* Clicking the card itself also opens */
      this.cards.forEach(card => {
        card.addEventListener('click', () => {
          wsrViewer.open(parseInt(card.dataset.reelIndex));
        });
      });
    }
  }

  /* ══════════════════════════════════════════════════════
     CLASS: WatchShopReelsViewer
     Manages the full-screen reel viewer
  ══════════════════════════════════════════════════════ */
  class WatchShopReelsViewer {
    constructor() {
      this.viewer      = qs('#wsr-viewer');
      if (!this.viewer) return;

      this.reels        = qsa('.wsr-reel',          this.viewer);
      this.currentIndex = 0;
      this.isOpen       = false;
      this.touchStartY  = 0;
      this.touchStartX  = 0;
      this.progressTimers = new Map();

      this._readSettings();
      this._bind();
    }

    _readSettings() {
      const v = this.viewer;
      this.infinite       = v.dataset.infinite !== 'false';
      this.enableShare    = v.dataset.enableShare !== 'false';
      this.enableWishlist = v.dataset.enableWishlist !== 'false';
      this.initialCount   = parseInt(v.dataset.initialCount) || 1;
    }

    /* ── Open viewer at index ── */
    open(index = 0) {
      if (!this.viewer) return;
      this.viewer.hidden  = false;
      this.viewer.classList.add('is-open');
      this.isOpen         = true;
      document.body.style.overflow = 'hidden';

      // Update counter total
      const totalEl = qs('[data-total-reels]', this.viewer);
      if (totalEl) totalEl.textContent = this.reels.length;

      this._goTo(index, false);
    }

    /* ── Close viewer ── */
    close() {
      if (!this.isOpen) return;
      this._pauseAll();
      this.viewer.classList.remove('is-open');
      this.viewer.classList.add('is-closing');
      setTimeout(() => {
        this.viewer.hidden = true;
        this.viewer.classList.remove('is-closing');
        this.isOpen = false;
        document.body.style.overflow = '';
      }, 300);
    }

    /* ── Navigate to reel ── */
    _goTo(index, animate = true) {
      const count = this.reels.length;
      if (!count) return;

      // Clamp or wrap
      if (this.infinite) {
        index = ((index % count) + count) % count;
      } else {
        index = clamp(index, 0, count - 1);
      }

      // Deactivate current
      const prev = this.reels[this.currentIndex];
      if (prev && prev !== this.reels[index]) {
        this._pauseReel(prev);
        prev.classList.remove('is-active');
        if (animate && index > this.currentIndex) {
          prev.classList.add('slide-up');
          setTimeout(() => prev.classList.remove('slide-up'), 400);
        }
      }

      this.currentIndex = index;
      const reel = this.reels[index];
      reel.classList.add('is-active');
      this._playReel(reel);

      // Update counter
      const curEl = qs('[data-current-reel]', this.viewer);
      if (curEl) curEl.textContent = index + 1;

      // Update nav button states
      const upBtn   = qs('[data-viewer-prev]', this.viewer);
      const downBtn = qs('[data-viewer-next]', this.viewer);
      if (!this.infinite) {
        if (upBtn)   upBtn.disabled   = index === 0;
        if (downBtn) downBtn.disabled = index === count - 1;
      }
    }

    /* ── Play a reel's video ── */
    _playReel(reel) {
      const video   = qs('.wsr-reel__video', reel);
      const progress= qs('[data-progress]', reel);
      if (!video) return;

      video.currentTime = 0;
      video.play().then(() => {
        this._startProgress(video, progress, reel);
      }).catch(() => {});
    }

    /* ── Pause a reel's video ── */
    _pauseReel(reel) {
      const video = qs('.wsr-reel__video', reel);
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      const progress = qs('[data-progress]', reel);
      if (progress) progress.style.width = '0%';
      this._clearProgress(reel);
    }

    _pauseAll() {
      this.reels.forEach(r => this._pauseReel(r));
    }

    /* ── Progress bar ── */
    _startProgress(video, bar, reel) {
      this._clearProgress(reel);
      if (!bar) return;

      const update = () => {
        if (video.paused || video.ended) return;
        const pct = (video.currentTime / video.duration) * 100;
        bar.style.width = pct + '%';
        this.progressTimers.set(reel, requestAnimationFrame(update));
      };

      this.progressTimers.set(reel, requestAnimationFrame(update));

      video.addEventListener('ended', () => {
        bar.style.width = '0%';
        if (this.infinite) this._goTo(this.currentIndex + 1);
      }, { once: true });
    }

    _clearProgress(reel) {
      const id = this.progressTimers.get(reel);
      if (id) { cancelAnimationFrame(id); this.progressTimers.delete(reel); }
    }

    /* ── Bind all events ── */
    _bind() {
      /* Close buttons / backdrop */
      qsa('[data-viewer-close]', this.viewer).forEach(el => {
        el.addEventListener('click', () => this.close());
      });

      /* Nav buttons */
      const prevBtn = qs('[data-viewer-prev]', this.viewer);
      const nextBtn = qs('[data-viewer-next]', this.viewer);
      if (prevBtn) prevBtn.addEventListener('click', () => this._goTo(this.currentIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this._goTo(this.currentIndex + 1));

      /* Keyboard */
      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape')    this.close();
        if (e.key === 'ArrowUp')   this._goTo(this.currentIndex - 1);
        if (e.key === 'ArrowDown') this._goTo(this.currentIndex + 1);
      });

      /* Touch swipe on viewer (ignore swipes that start inside the scrollable content area) */
      const reelsEl = qs('[data-viewer-reels]', this.viewer);
      if (reelsEl) {
        reelsEl.addEventListener('touchstart', e => {
          this.touchStartY = e.touches[0].clientY;
          this.touchStartX = e.touches[0].clientX;
          this.touchInScroll = !!e.target.closest('[data-reel-scroll]');
        }, { passive: true });

        reelsEl.addEventListener('touchend', e => {
          if (this.touchInScroll) return; // let content scroll normally
          const dy = this.touchStartY - e.changedTouches[0].clientY;
          const dx = Math.abs(this.touchStartX - e.changedTouches[0].clientX);
          if (Math.abs(dy) < 50 || dx > 60) return; // not a vertical swipe
          if (dy > 0) this._goTo(this.currentIndex + 1);
          else        this._goTo(this.currentIndex - 1);
        });
      }

      /* Tap-to-pause on tap zones */
      qsa('[data-tap-zone]', this.viewer).forEach(zone => {
        zone.addEventListener('click', () => {
          const reel  = zone.closest('.wsr-reel');
          if (!reel) return;
          const video = qs('.wsr-reel__video', reel);
          const icon  = qs('[data-pause-icon]', reel);
          if (!video) return;

          if (video.paused) {
            video.play().catch(() => {});
            if (icon) { icon.classList.add('is-visible'); setTimeout(() => icon.classList.remove('is-visible'), 700); }
          } else {
            video.pause();
            if (icon) icon.classList.add('is-visible');
          }
        });
      });

      /* Share buttons */
      qsa('[data-action="share"]', this.viewer).forEach(btn => {
        btn.addEventListener('click', async () => {
          const title = btn.dataset.reelTitle;
          const url   = btn.dataset.reelUrl || window.location.href;
          if (navigator.share) {
            try { await navigator.share({ title, url }); } catch (_) {}
          } else {
            await navigator.clipboard.writeText(url).catch(() => {});
            this._showToast('Link copied to clipboard');
          }
        });
      });

      /* Wishlist buttons — actual save/remove is handled by the theme's own
         site-wide wishlist script, which listens for clicks on elements with
         the `.wishlist-custom-action` / `.active-wishlist` classes and a
         `data-handle` attribute (see theme wishlist.js). We just reflect the
         resulting state back into this widget's UI (toast + aria-pressed). */
      qsa('[data-action="wishlist"]', this.viewer).forEach(btn => {
        if (btn.disabled) return;
        btn.addEventListener('click', () => {
          // Wait a tick so the theme's document-level delegated handler
          // (which runs during the bubble phase, after this listener) has
          // already toggled the wishlist-custom-action/active-wishlist class.
          setTimeout(() => {
            const isWL = btn.classList.contains('active-wishlist');
            btn.setAttribute('aria-pressed', String(isWL));
            this._showToast(isWL ? 'Added to wishlist' : 'Removed from wishlist');
            document.dispatchEvent(new CustomEvent('wsr:wishlist', {
              detail: { handle: btn.dataset.handle, reelId: btn.dataset.reelId, wishlisted: isWL }
            }));
          }, 0);
        });
      });

      /* Product reveal — "See More" */
      qsa('[data-reveal-products]', this.viewer).forEach(btn => {
        btn.addEventListener('click', () => {
          const track = btn.closest('[data-products-track]');
          if (!track) return;
          qsa('.wsr-product-card--hidden', track).forEach(c => c.classList.remove('wsr-product-card--hidden'));
          btn.closest('.wsr-product-card--more').style.display = 'none';
        });
      });

      /* Add-to-cart buttons */
      qsa('[data-product-id]', this.viewer).forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const variantId = btn.dataset.productId;
          if (!variantId) return;
          btn.disabled = true;
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body:    JSON.stringify({ id: variantId, quantity: 1 })
            });
            if (res.ok) {
              this._showToast('Added to cart');
              document.dispatchEvent(new CustomEvent('cart:refresh'));
              // Update cart count bubble if present
              fetch('/cart.js').then(r => r.json()).then(cart => {
                document.querySelectorAll('[data-cart-count]').forEach(el => {
                  el.textContent = cart.item_count;
                });
              });
            } else {
              this._showToast('Could not add to cart');
            }
          } catch (_) {
            this._showToast('Could not add to cart');
          } finally {
            btn.disabled = false;
          }
        });
      });
    }

    /* ── Toast notification ── */
    _showToast(message) {
      let toast = qs('.wsr-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'wsr-toast';
        document.body.appendChild(toast);
        Object.assign(toast.style, {
          position:    'fixed',
          bottom:      '80px',
          left:        '50%',
          transform:   'translateX(-50%) translateY(16px)',
          background:  'rgba(10,10,10,0.92)',
          color:       '#F5F0E8',
          fontFamily:  '"Jost","Helvetica Neue",sans-serif',
          fontSize:    '12px',
          letterSpacing:'0.05em',
          padding:     '10px 20px',
          borderRadius:'20px',
          border:      '1px solid rgba(201,169,110,0.3)',
          zIndex:      '99999',
          opacity:     '0',
          transition:  'opacity 0.3s ease, transform 0.3s ease',
          pointerEvents:'none',
          whiteSpace:  'nowrap',
        });
      }
      toast.textContent = message;
      requestAnimationFrame(() => {
        toast.style.opacity   = '1';
        toast.style.transform = 'translateX(-50%) translateY(0)';
      });
      clearTimeout(toast._timer);
      toast._timer = setTimeout(() => {
        toast.style.opacity   = '0';
        toast.style.transform = 'translateX(-50%) translateY(8px)';
      }, 2500);
    }
  }

  /* ══════════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════════ */

  /* Singleton viewer */
  let wsrViewer;

  function init() {
    wsrViewer = new WatchShopReelsViewer();

    qsa('.wsr-section').forEach(section => {
      new WatchShopReels(section);
    });
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify Theme Editor live-reload support */
  document.addEventListener('shopify:section:load',   init);
  document.addEventListener('shopify:section:reorder', init);

})();
